import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp, Calendar, Youtube, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import GameCountdown from '../components/GameCountdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JacksonStatePage = () => {
  const [articles, setArticles] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('news');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, scheduleRes, youtubeRes] = await Promise.all([
          axios.get(`${API}/articles?category=Jackson State&limit=6`),
          axios.get(`${API}/schedule`),
          axios.get(`${API}/youtube/playlist`)
        ]);
        setArticles(articlesRes.data);
        setSchedule(scheduleRes.data);
        if (youtubeRes.data.videos) {
          setYoutubeVideos(youtubeRes.data.videos.slice(0, 4));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Find first non-bye game for countdown
  const firstGame = schedule.find(g => g.opponent !== "OFF");

  return (
    <div className="min-h-screen" data-testid="jackson-state-page">
      <GameCountdown 
        gameDate="08/29/2026"
        opponent="Tennessee State"
        isHome={false}
      />
      
      <div className="relative h-[40vh] bg-[#001f3f] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#001f3f] to-[#003d7a]"></div>
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <div>
            <Trophy className="h-16 w-16 text-[#DC2626] mx-auto mb-4" />
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase text-white mb-2 heading-font" data-testid="jsu-title">
              Jackson State
            </h1>
            <p className="text-xl text-[#DC2626] font-bold uppercase tracking-wider">Tigers Football</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex">
            <button
              onClick={() => setActiveTab('news')}
              className={`flex items-center gap-2 px-6 py-4 font-bold uppercase tracking-wider text-sm border-b-4 transition-colors ${
                activeTab === 'news'
                  ? 'border-[#DC2626] text-[#DC2626]'
                  : 'border-transparent text-slate-600 hover:text-[#001f3f]'
              }`}
              data-testid="news-tab"
            >
              <TrendingUp className="h-5 w-5" />
              News
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`flex items-center gap-2 px-6 py-4 font-bold uppercase tracking-wider text-sm border-b-4 transition-colors ${
                activeTab === 'schedule'
                  ? 'border-[#DC2626] text-[#DC2626]'
                  : 'border-transparent text-slate-600 hover:text-[#001f3f]'
              }`}
              data-testid="schedule-tab"
            >
              <Calendar className="h-5 w-5" />
              2026 Schedule
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12" data-testid="loading">Loading...</div>
        ) : activeTab === 'news' ? (
          /* News Section */
          <div>
            {articles.length === 0 ? (
              <div className="text-center py-12 text-slate-600" data-testid="no-articles">
                No Jackson State articles yet. Check back soon!
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {articles.map(article => (
                  <a
                    key={article.article_id}
                    href={`/news/${article.article_id}`}
                    className="group bg-white border border-slate-200 hover:border-[#007AFF] transition-all shadow-sm hover:shadow-xl overflow-hidden"
                  >
                    {article.image_url && (
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={article.image_url} 
                          alt={article.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold uppercase mb-2 heading-font text-[#001f3f] line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt}</p>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {/* YouTube Videos */}
            {youtubeVideos.length > 0 && (
              <div className="mt-8">
                <h2 className="text-3xl font-bold tracking-tight uppercase text-[#001f3f] mb-6 heading-font flex items-center">
                  <Youtube className="h-8 w-8 mr-3 text-red-600" />
                  Video Highlights
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {youtubeVideos.map(video => (
                    <div key={video.video_id} className="bg-white shadow-lg overflow-hidden">
                      <div className="aspect-video">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${video.video_id}`}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-[#001f3f] line-clamp-2">{video.title}</h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Schedule Section */
          <div>
            <h2 className="text-3xl font-bold tracking-tight uppercase text-[#001f3f] mb-6 heading-font text-center">
              2026 Football Schedule
            </h2>
            
            {schedule.length === 0 ? (
              <div className="text-center py-12 text-slate-600">
                Schedule coming soon!
              </div>
            ) : (
              <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <div className="divide-y divide-slate-200">
                  {schedule.map(game => (
                    <GameRow key={game.game_id} game={game} />
                  ))}
                </div>
              </div>
            )}
            
            <div className="mt-6 text-center text-sm text-slate-600">
              <p>* denotes SWAC Conference game</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const GameRow = ({ game }) => {
  if (game.opponent === "OFF") {
    return (
      <div className="p-4 bg-slate-50">
        <div className="flex items-center justify-between">
          <span className="font-bold text-slate-700">{game.date}</span>
          <span className="text-slate-500 italic">BYE WEEK</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 hover:bg-slate-50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          <span className="font-bold text-slate-700 w-16">{game.date}</span>
          
          <div className="flex items-center space-x-3 flex-1">
            {game.opponent_logo ? (
              <img 
                src={game.opponent_logo} 
                alt={game.opponent}
                className="h-10 w-10 object-contain"
              />
            ) : (
              <div className="h-10 w-10 bg-slate-200 rounded flex items-center justify-center flex-shrink-0">
                <span className="text-xs text-slate-400 font-bold">LOGO</span>
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                {!game.is_home && <span className="text-sm text-slate-600">at</span>}
                <span className="font-semibold text-slate-900">{game.opponent}</span>
                {game.is_swac && <span className="text-[#DC2626] text-sm">*</span>}
              </div>
              {game.location && (
                <div className="flex items-center space-x-1 text-xs text-slate-500 mt-1">
                  <MapPin className="h-3 w-3" />
                  <span>{game.location}</span>
                </div>
              )}
              {game.game_name && (
                <span className="text-xs text-[#007AFF] font-semibold">{game.game_name}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="text-right ml-4">
          <span className="text-sm font-semibold text-slate-700">{game.kickoff_time}</span>
          {game.tv && (
            <div className="text-xs text-slate-500">{game.tv}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JacksonStatePage;
